/* ─── NotificationBell — bell icon + dropdown ────────────────────── */

import { useState, useEffect, useRef } from 'react';
import { Bell, BellOff } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getNotificationLink,
  getNotificationIcon,
} from '../services/notificationService';
import { getFeatherIcon } from '../utils/iconMap';
import type { AppNotification } from '../types/social';

function timeAgo(unixSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 604800)}w`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.readStatus).length;

  // Subscribe to notifications
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = subscribeToNotifications(
      user.uid,
      (items) => setNotifications(items),
      (err) => console.error('Notification listener error:', err)
    );
    return unsub;
  }, [user?.uid]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!user) return null;

  async function handleNotificationClick(n: AppNotification) {
    if (!n.readStatus) {
      await markNotificationRead(user!.uid, n.id);
    }
    setOpen(false);
    navigate(getNotificationLink(n));
  }

  async function handleMarkAllRead() {
    if (!user?.uid) return;
    await markAllNotificationsRead(user.uid);
  }

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={() => setOpen(!open)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={18} aria-hidden="true" />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>notifications</h3>
            {unreadCount > 0 && (
              <button className="notification-mark-all" onClick={handleMarkAllRead}>
                mark all read
              </button>
            )}
          </div>

          <div className="notification-dropdown-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <BellOff size={24} />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  className={`notification-item ${!n.readStatus ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <div className="notification-item-icon">
                    {(() => { const Icon = getFeatherIcon(getNotificationIcon(n.type)); return <Icon size={16} />; })()}
                  </div>
                  <div className="notification-item-content">
                    <span className="notification-item-title">{n.title}</span>
                    <span className="notification-item-desc">{n.description}</span>
                  </div>
                  <span className="notification-item-time">{timeAgo(n.createdDate)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
